import { useState, useEffect } from 'react'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faPlus, faTrash, faSave, faEye, faEyeSlash } from '@fortawesome/free-solid-svg-icons'
import { LoadingSpinner } from '../../shared/components/ui/LoadingSpinner'
import Button from '../../shared/components/ui/Button'
import Input from '../../shared/components/ui/Input'
import { useAccountContext } from '../../contexts'

// Alert types from monorepo
enum AlertType {
  SENSOR_ALERTS = 'SENSOR_ALERTS',
  SYSTEM_CONTROL_MESSAGES = 'SYSTEM_CONTROL_MESSAGES',
  TEST_MESSAGES = 'TEST_MESSAGES',
}

enum ContactType {
  EMAIL = 'EMAIL',
  SMS = 'SMS',
}

interface AlertSettingsItem {
  id?: number
  enabled: boolean
  itemOrder: number
  deviceId: number
  alertType: AlertType
  contactType: ContactType
  contact: string
  message: string
  totalAttempts: number
  retryDelay: number
  changed?: boolean
}

const BASE_ALERT_SETTINGS: Omit<AlertSettingsItem, 'alertType' | 'contactType' | 'itemOrder'> = {
  enabled: false,
  deviceId: 0,
  contact: '',
  message: 'Alert from device {deviceName}: {alertMessage}',
  totalAttempts: 3,
  retryDelay: 15,
  changed: false,
}

export function AlertSettings() {
  const { devices } = useAccountContext()
  const [selectedDeviceId, setSelectedDeviceId] = useState<number>(0)
  const [advancedMode, setAdvancedMode] = useState(false)
  const [localSettings, setLocalSettings] = useState<AlertSettingsItem[]>([])
  const [isLoading, setIsLoading] = useState(false)
  const [isSaving, setIsSaving] = useState(false)

  // Load advanced mode preference from localStorage
  useEffect(() => {
    const saved = localStorage.getItem('alert-advanced-mode')
    if (saved) {
      setAdvancedMode(JSON.parse(saved))
    }
  }, [])

  // Load settings when device changes
  useEffect(() => {
    const fetchSettings = async () => {
      setIsLoading(true)

      try {
        // TODO: Replace with actual API call from webapp services when available
        // Example: const response = await api.getAlertSettings(selectedDeviceId)
        // For now, initialize with empty array
        setLocalSettings([])
      } catch (error) {
        console.error('Failed to load alert settings:', error)
      } finally {
        setIsLoading(false)
      }
    }

    fetchSettings()
  }, [selectedDeviceId])

  const devicesArray = Object.values(devices)
  const visibleAlertTypes = advancedMode ? Object.values(AlertType) : [AlertType.SENSOR_ALERTS]

  const handleAdvancedModeToggle = (enabled: boolean) => {
    setAdvancedMode(enabled)
    localStorage.setItem('alert-advanced-mode', JSON.stringify(enabled))
  }

  const handleAddSetting = () => {
    const newSetting: AlertSettingsItem = {
      ...BASE_ALERT_SETTINGS,
      deviceId: selectedDeviceId,
      alertType: AlertType.SENSOR_ALERTS,
      contactType: ContactType.EMAIL,
      itemOrder: getNextItemOrder(AlertType.SENSOR_ALERTS, ContactType.EMAIL),
      changed: true,
    }
    setLocalSettings([...localSettings, newSetting])
  }

  const getNextItemOrder = (alertType: AlertType, contactType: ContactType): number => {
    const existing = localSettings.filter(
      (s) => s.alertType === alertType && s.contactType === contactType && s.deviceId === selectedDeviceId
    )
    const maxOrder = Math.max(0, ...existing.map((s) => s.itemOrder))
    return Math.min(maxOrder + 1, 3)
  }

  const handleSettingChange = (index: number, field: keyof AlertSettingsItem, value: any) => {
    const updated = [...localSettings]
    updated[index] = { ...updated[index], [field]: value, changed: true }
    setLocalSettings(updated)
  }

  const handleSaveSetting = async (setting: AlertSettingsItem, index: number) => {
    if (!setting.changed) return

    setIsSaving(true)
    try {
      // TODO: Replace with actual API call from webapp services when available
      // Example: await api.updateAlertSettings(selectedDeviceId, [setting])
      console.log('Saving alert setting:', setting)

      const updated = [...localSettings]
      updated[index] = { ...updated[index], changed: false }
      setLocalSettings(updated)
    } catch (error) {
      console.error('Failed to save setting:', error)
    } finally {
      setIsSaving(false)
    }
  }

  const handleDeleteSetting = async (setting: AlertSettingsItem, index: number) => {
    const updated = localSettings.filter((_, i) => i !== index)
    setLocalSettings(updated)

    if (setting.id) {
      try {
        // TODO: Replace with actual API call to delete setting
        console.log('Deleting alert setting:', setting.id)
      } catch (error) {
        console.error('Failed to delete setting:', error)
        setLocalSettings(localSettings)
      }
    }
  }

  if (isLoading) {
    return (
      <div className="p-6 flex justify-center">
        <LoadingSpinner size="lg" />
      </div>
    )
  }

  return (
    <div className="p-6 space-y-6">
      {/* Controls */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between space-y-4 sm:space-y-0">
        <div className="flex items-center space-x-4">
          <div>
            <label className="block text-sm font-medium text-gray-700">Device</label>
            <select
              value={selectedDeviceId}
              onChange={(e) => setSelectedDeviceId(Number(e.target.value))}
              className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 sm:text-sm px-3 py-2 border"
            >
              <option value={0}>Global Defaults</option>
              {devicesArray.map((device: any) => (
                <option key={device.id} value={device.id}>
                  {device.name || `Device ${device.id}`}
                </option>
              ))}
            </select>
          </div>

          <div className="flex items-center">
            <button
              onClick={() => handleAdvancedModeToggle(!advancedMode)}
              className="flex items-center space-x-2 text-sm text-gray-600 hover:text-gray-900"
            >
              <FontAwesomeIcon icon={advancedMode ? faEye : faEyeSlash} />
              <span>{advancedMode ? 'Basic Mode' : 'Advanced Mode'}</span>
            </button>
          </div>
        </div>

        <Button onClick={handleAddSetting} variant="primary">
          <FontAwesomeIcon icon={faPlus} className="mr-2 h-4 w-4" />
          Add Setting
        </Button>
      </div>

      {/* Settings Table */}
      <div className="overflow-x-auto">
        <table className="min-w-full divide-y divide-gray-200">
          <thead className="bg-gray-50">
            <tr>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Enabled
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Alert Type
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Contact Type
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Order</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Contact
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Message
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Attempts
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Delay (min)
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Actions
              </th>
            </tr>
          </thead>
          <tbody className="bg-white divide-y divide-gray-200">
            {localSettings
              .filter((setting) => visibleAlertTypes.includes(setting.alertType))
              .map((setting, index) => (
                <tr key={`${setting.id || 'new'}-${index}`} className={setting.changed ? 'bg-yellow-50' : ''}>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <input
                      type="checkbox"
                      checked={setting.enabled}
                      onChange={(e) => handleSettingChange(index, 'enabled', e.target.checked)}
                      className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                    />
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <select
                      value={setting.alertType}
                      onChange={(e) => handleSettingChange(index, 'alertType', e.target.value)}
                      className="text-sm border-gray-300 rounded-md px-2 py-1 border"
                    >
                      {visibleAlertTypes.map((type) => (
                        <option key={type} value={type}>
                          {type.replace(/_/g, ' ')}
                        </option>
                      ))}
                    </select>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <select
                      value={setting.contactType}
                      onChange={(e) => handleSettingChange(index, 'contactType', e.target.value)}
                      className="text-sm border-gray-300 rounded-md px-2 py-1 border"
                    >
                      {Object.values(ContactType).map((type) => (
                        <option key={type} value={type}>
                          {type}
                        </option>
                      ))}
                    </select>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <select
                      value={setting.itemOrder}
                      onChange={(e) => handleSettingChange(index, 'itemOrder', Number(e.target.value))}
                      className="text-sm border-gray-300 rounded-md px-2 py-1 border"
                    >
                      <option value={1}>1</option>
                      <option value={2}>2</option>
                      <option value={3}>3</option>
                    </select>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <Input
                      type={setting.contactType === ContactType.EMAIL ? 'email' : 'tel'}
                      value={setting.contact}
                      onChange={(e) => handleSettingChange(index, 'contact', e.target.value)}
                      placeholder={setting.contactType === ContactType.EMAIL ? 'email@example.com' : '+1234567890'}
                      className="text-sm w-full"
                    />
                  </td>
                  <td className="px-6 py-4">
                    <Input
                      type="text"
                      value={setting.message}
                      onChange={(e) => handleSettingChange(index, 'message', e.target.value)}
                      className="text-sm w-full"
                    />
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <input
                      type="number"
                      min="1"
                      max="10"
                      value={setting.totalAttempts}
                      onChange={(e) => handleSettingChange(index, 'totalAttempts', Number(e.target.value))}
                      className="text-sm border-gray-300 rounded-md w-16 px-2 py-1 border"
                    />
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <input
                      type="number"
                      min="1"
                      max="1440"
                      value={setting.retryDelay}
                      onChange={(e) => handleSettingChange(index, 'retryDelay', Number(e.target.value))}
                      className="text-sm border-gray-300 rounded-md w-16 px-2 py-1 border"
                    />
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium space-x-2">
                    {setting.changed && (
                      <button
                        onClick={() => handleSaveSetting(setting, index)}
                        disabled={isSaving}
                        className="text-green-600 hover:text-green-900 disabled:opacity-50"
                      >
                        <FontAwesomeIcon icon={faSave} />
                      </button>
                    )}
                    <button
                      onClick={() => handleDeleteSetting(setting, index)}
                      disabled={isSaving}
                      className="text-red-600 hover:text-red-900 disabled:opacity-50"
                    >
                      <FontAwesomeIcon icon={faTrash} />
                    </button>
                  </td>
                </tr>
              ))}
          </tbody>
        </table>
      </div>

      {localSettings.filter((s) => visibleAlertTypes.includes(s.alertType)).length === 0 && (
        <div className="text-center py-8">
          <p className="text-gray-500">
            No alert settings configured. Click "Add Setting" to create your first alert configuration.
          </p>
        </div>
      )}
    </div>
  )
}
